// BIGMAP (owner 2026-11-02: the arena grew to 160 m, ~2.1x the area, so ~1100 props on map #1). Render-only chunked culling
// for arena-spread static instances: items (props, decals) are bucketed once per layout into square chunks; every frame the
// camera's ground footprint (plus a margin for tall props, and the shadow camera's reach on high) picks the chunks worth
// drawing, and the caller fills its instance buffers from those chunks only. No per-frame allocation: the buckets are a
// counting sort into preallocated typed arrays, the visible chunk list is a reused Int32Array.
//
// QA: `?cull=0` turns culling off (everything drawn, as before); `CULL.radius` / `CULL.off` can be set from QA routes.
import * as THREE from 'three/webgpu';

/** global switches (QA routes and ?cull=0) */
export const CULL = {
  off: typeof location !== 'undefined' && new URLSearchParams(location.search).get('cull') === '0',
  /** extra margin (m) added around the footprint for the widest / tallest props (spires are ~8 m across) */
  margin: 12,
  /** stats for the perf overlay / QA: chunks visible / total, items considered last frame */
  stats: { chunks: 0, total: 0, items: 0 },
};

const _ray = new THREE.Vector3(), _o = new THREE.Vector3(), _ndc = new THREE.Vector3();
const CORNERS: [number, number][] = [[-1, -1], [1, -1], [1, 1], [-1, 1], [0, 1], [0, -1]];

/** Axis-aligned ground box (x0..x1, z0..z1). */
export interface GroundBox { x0: number; z0: number; x1: number; z1: number }

/** The xz box the camera can see on the ground plane y = 0 (rays that miss the ground reach `far`), grown by `margin` and
 *  including the camera's own xz (tall things between the camera and the ground footprint). Optionally unioned with a
 *  square of half-size `focusR` around (fx, fz) (the shadow camera follows the hero). */
export function viewBox(camera: THREE.PerspectiveCamera, margin: number, out: GroundBox, fx = 0, fz = 0, focusR = 0, far = 420): GroundBox {
  camera.updateMatrixWorld();
  _o.setFromMatrixPosition(camera.matrixWorld);
  let x0 = _o.x, x1 = _o.x, z0 = _o.z, z1 = _o.z;
  for (const [cx, cy] of CORNERS) {
    _ndc.set(cx, cy, 0.5).unproject(camera);
    _ray.copy(_ndc).sub(_o).normalize();
    // distance along the ray to y = 0 (ground and the hills sit within a few metres of it); a ray that never comes down
    // (sky) or comes down very far is clamped to `far`
    let t = _ray.y < -1e-3 ? -_o.y / _ray.y : far;
    if (t > far || t < 0) t = far;
    const x = _o.x + _ray.x * t, z = _o.z + _ray.z * t;
    if (x < x0) x0 = x; if (x > x1) x1 = x; if (z < z0) z0 = z; if (z > z1) z1 = z;
  }
  out.x0 = x0 - margin; out.x1 = x1 + margin; out.z0 = z0 - margin; out.z1 = z1 + margin;
  if (focusR > 0) {
    out.x0 = Math.min(out.x0, fx - focusR); out.x1 = Math.max(out.x1, fx + focusR);
    out.z0 = Math.min(out.z0, fz - focusR); out.z1 = Math.max(out.z1, fz + focusR);
  }
  return out;
}

/** Square-chunk buckets over [-half, half]² (items outside go to the edge chunks; they are still drawn at their real spot). */
export class ChunkGrid {
  readonly n: number;
  /** chunk c holds items[start[c] .. start[c + 1]) */
  private start: Int32Array;
  private items = new Int32Array(0);
  private cellOf = new Int32Array(0);
  /** positions the buckets were built from (a moved item triggers a rebuild) */
  private bx = new Float64Array(0); private bz = new Float64Array(0);
  private count = 0;
  /** visible chunk ids from the last select() */
  readonly vis: Int32Array; visN = 0;

  constructor(readonly half: number, readonly size = 24) {
    this.n = Math.ceil((half * 2) / size);
    this.start = new Int32Array(this.n * this.n + 1);
    this.vis = new Int32Array(this.n * this.n);
  }

  private cell(x: number, z: number): number {
    const n = this.n, i = Math.min(n - 1, Math.max(0, Math.floor((x + this.half) / this.size))), j = Math.min(n - 1, Math.max(0, Math.floor((z + this.half) / this.size)));
    return j * n + i;
  }

  /** (Re)bucket `count` items whose positions come from pos(i) (counting sort; allocates only when the count grows). */
  build(count: number, px: (i: number) => number, pz: (i: number) => number): void {
    if (this.items.length < count) { const cap = Math.max(count, 16); this.items = new Int32Array(cap); this.cellOf = new Int32Array(cap); this.bx = new Float64Array(cap); this.bz = new Float64Array(cap); }
    this.count = count;
    const st = this.start; st.fill(0);
    for (let i = 0; i < count; i++) { const x = px(i), z = pz(i); this.bx[i] = x; this.bz[i] = z; const c = this.cell(x, z); this.cellOf[i] = c; st[c + 1]++; }
    for (let c = 0; c < this.n * this.n; c++) st[c + 1] += st[c];
    // fill (use vis as a scratch write cursor per chunk is not enough space-wise when n*n < count; walk with a cursor copy)
    const cur = this.cursor.length >= this.n * this.n ? this.cursor : (this.cursor = new Int32Array(this.n * this.n));
    for (let c = 0; c < this.n * this.n; c++) cur[c] = st[c];
    for (let i = 0; i < count; i++) this.items[cur[this.cellOf[i]]++] = i;
  }
  private cursor = new Int32Array(0);

  /** true when any item moved off the position it was bucketed at (QA staging moves props; the layout never does) */
  moved(count: number, px: (i: number) => number, pz: (i: number) => number): boolean {
    if (count !== this.count) return true;
    for (let i = 0; i < count; i++) if (px(i) !== this.bx[i] || pz(i) !== this.bz[i]) return true;
    return false;
  }

  /** pick the chunks overlapping the box (null / CULL.off = every chunk) */
  select(box: GroundBox | null): void {
    const n = this.n; let k = 0;
    if (!box || CULL.off) { for (let c = 0; c < n * n; c++) if (this.start[c + 1] > this.start[c]) this.vis[k++] = c; this.visN = k; return; }
    const s = this.size, h = this.half;
    // the edge chunks also hold everything outside the grid: include a whole edge row / column once the box reaches it
    const i0 = Math.max(0, Math.floor((box.x0 + h) / s)), i1 = Math.min(n - 1, Math.floor((box.x1 + h) / s));
    const j0 = Math.max(0, Math.floor((box.z0 + h) / s)), j1 = Math.min(n - 1, Math.floor((box.z1 + h) / s));
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) { const c = j * n + i; if (this.start[c + 1] > this.start[c]) this.vis[k++] = c; }
    this.visN = k;
  }

  /** visit every item in the selected chunks */
  forVisible(fn: (i: number) => void): number {
    let m = 0;
    for (let v = 0; v < this.visN; v++) { const c = this.vis[v]; for (let q = this.start[c]; q < this.start[c + 1]; q++) { fn(this.items[q]); m++; } }
    return m;
  }
  /** start / end offsets for manual loops without a closure (hot paths) */
  chunkStart(c: number): number { return this.start[c]; }
  chunkEnd(c: number): number { return this.start[c + 1]; }
  item(q: number): number { return this.items[q]; }
  get chunks(): number { return this.n * this.n; }
}

/** a box that contains everything (culling off) */
export const ALL_BOX: GroundBox = { x0: -1e9, z0: -1e9, x1: 1e9, z1: 1e9 };
export const inBox = (b: GroundBox, x: number, z: number, r = 0): boolean => x + r >= b.x0 && x - r <= b.x1 && z + r >= b.z0 && z - r <= b.z1;
